import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Tag, Input, Space, Table } from '@bedrock/components';
import { Edit, Eye } from '@bedrock/icons-react';
import { apiService, defs } from '@mpc/common/src/services';
import { useMemoizedFn, useUpdateEffect, useDeepCompareLayoutEffect, useDebounceFn } from 'ahooks';
import { QuoteTableDrawerOpen } from '../DemandConfigQuoteTable/quoteDrawer';
import { createRowByCooperateAccountId, createPreFillValueWithStandardId } from '../DemandConfigQuoteTable/handleQuoteColumns';



export default function (props: {
  value?: any;
  onChange?: (value: any) => void;
  categoryId: number;
  demandId: number;
  cooperateAccountList: defs.DemandCooperateAccountDto[];
  view?: boolean;
}) {
  const { value = null, onChange, categoryId, demandId, cooperateAccountList, view = false } = props;
  const [columnsRes, setColumnsRes] = useState<defs.DemandQuoteTableDto | null>(
    null
  );
  const [opening, setOpening] = useState(false);

  const localOnChange = useMemoizedFn((newValue) => {
    !view && onChange?.(newValue);
  });

  useEffect(() => {
    if (categoryId && !view) {
      apiService
        .demandCategoryPropertyQuoteTable({
          category_id: categoryId,
        })
        .then((res) => {
          setColumnsRes(res.data);
        });
    }
  }, [categoryId, view]);

  const newCooperateAccountList = useMemo(() => {
    return cooperateAccountList?.map((item, index) => {
      if (view) {
        return item;
      }
      return {
        ...item,
        id: -index - 1,
        index_id: index + 1,
        o_id: item.id || -index - 1,
      };
    });
  }, [cooperateAccountList, view]);

  const {run: resetValue} = useDebounceFn(() => {
    if(!columnsRes || view) return
    if (columnsRes?.template_type === 1) {
      const { columns } = columnsRes;
      const newValue = newCooperateAccountList?.map((item, index) => {
        return createRowByCooperateAccountId(
          columns,
          newCooperateAccountList,
          item.id
        );
      });
      localOnChange(newValue);
    } else {
      localOnChange(null);
    }
  }, {
    wait: 1000,
    leading: false,
    trailing: true,
  });

  useUpdateEffect(() => {
    // 如果categoryId或者newCooperateAccountList.length发生变化，则清空value  
    columnsRes?.template_type !== 1 && resetValue();
  }, [categoryId, newCooperateAccountList?.length]);

  useDeepCompareLayoutEffect(() => {
    // 如果是KOL则直接设置value
    columnsRes?.template_type === 1 && resetValue()
  }, [columnsRes, newCooperateAccountList]);

  const initValue = useMemoizedFn(() => {
    if (columnsRes?.template_type === 1 && newCooperateAccountList?.length) {
      resetValue()
    }
  });

  useEffect(() => {
    setTimeout(() => {
      initValue()
    }, 1000);
  }, []);


  const onOpenQuoteDrawer = useMemoizedFn(async () => {
    if (!columnsRes) {
      return;
    }
    setOpening(true);
    const { columns, template_type } = columnsRes;
    let newValue = value?.length
      ? value
        :createPreFillValueWithStandardId(columns, newCooperateAccountList);
    const newCooperateAccountMap = newCooperateAccountList?.reduce((acc, item) => {
      acc[item.o_id] = item.id;
      return acc;
    }, {})
    newValue = newValue.map((item: any) => {
      return {
        ...item,
        cooperateAccountId:  newCooperateAccountMap[item.cooperateAccountId] || item.cooperateAccountId,
      }
    })    
    const { close } = QuoteTableDrawerOpen.show({
      data: newValue,
      onCommit: (newData) => {
        localOnChange(newData);
        close();
      },
      onClose: () => {
        setOpening(false);
      },
      demandId: demandId,
      categoryId: categoryId,
      templateType: template_type,
      quoteTableColumns: columns,
      cooperateAccountList: newCooperateAccountList,
      quoteTableType: 'prefill',
    });
    setOpening(false);
  });

  if (view) {
    return (
      <Space size={8}>
        <Button
          size="small"
          type="checked-neutral"
          color="primary"
          icon={<Eye />}
          loading={opening}
          onClick={async () => {
            try {
              setOpening(true);
            // 通过接口获取预览数据
              const { data: resData } = await apiService.demandGetQuoteTableData({
                l3_category_id: categoryId,
                demand_id: demandId,
              });
              const { columns, template_type, pre_fill_data } = resData;
              const { close } = QuoteTableDrawerOpen.show({
                data: pre_fill_data,
                readOnly: true,
                templateType: template_type,
                quoteTableColumns: columns,
                cooperateAccountList: newCooperateAccountList,
                quoteTableType: 'prefill',
              });
              setOpening(false);
            } catch (error) {
              setOpening(false);setOpening(false);
            }
          }}
        >
          预览
        </Button>
      </Space>
    )
  }

  return (
    <Space size={8}>
      {columnsRes?.template_type === 1 && (
        <Button
          size="small"
          type="text"
          color="primary"
          icon={<Eye />}
          loading={opening}
          onClick={() => {
            if (!columnsRes) {
              return;
            }
            setOpening(true);
            const { columns, template_type } = columnsRes;
            const { close } = QuoteTableDrawerOpen.show({
              data: value,
              onCommit: (newData) => {
                close();
              },
              onClose: () => {
                setOpening(false);
              },
              readOnly: true,
              categoryId: categoryId,
              templateType: template_type,
              quoteTableColumns: columns,
              cooperateAccountList: newCooperateAccountList,
              quoteTableType: 'prefill',
            });
          }}
        >
          预览
        </Button>
      )}
      {columnsRes?.template_type !== 1 && (
        <>
          <Button
            size="small"
            type="checked"
            color="primary"
            icon={<Edit />}
            onClick={() => {
              onOpenQuoteDrawer();
            }}
            loading={opening}
          >
            预填写
          </Button>
          <Tag
            color={value?.length ? 'light-green' : 'light-orange'}
            size="small"
            text={value?.length ? '已填写' : '待填写'}
          />
        </>
      )}
    </Space>
  );
}

// 报价预览弹框
export async function previewQuoteDrawer(row: any) {
  const { data } = await apiService.demandCategoryPropertyQuoteTable({
    category_id: row.category_id,
  });
  const { columns, template_type } = data;
  const cooperateAccountList = [
    {
      id: -1,
      index_id: 1,
    },
    {
      id: -2,
      index_id: 2,
    },
  ];
  const newValue = cooperateAccountList?.map((item, index) => {
    return createRowByCooperateAccountId(
      columns,
      cooperateAccountList,
      item.id
    );
  });
  const { close } = QuoteTableDrawerOpen.show({
    title: `${row.category_names} - 报价模板`,
    data: newValue,
    templateType: template_type,
    quoteTableColumns: columns,
    cooperateAccountList: cooperateAccountList,
    quoteTableType: 'supplierQuote',
    onClose: () => {
      close();
    },
  });
  return {
    close,
  };
}
